import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  EnvelopeIcon, 
  MapPinIcon,
  ClockIcon,
  ChatBubbleLeftRightIcon,
  VideoCameraIcon,
  GlobeAltIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  AcademicCapIcon,
  WrenchScrewdriverIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  CalendarDaysIcon,
  CheckBadgeIcon,
  StarIcon,
  BoltIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';

// Import images
import officeImage from '../assets/teammates-working-late-office_23-2148991374.jpg_vty8zw.jpg';
import teamImage from '../assets/diverse-group-business-professionals-posing-together-monitoring-room_482257-94131.jpg_kxystx.jpg';
import analysisImage from '../assets/diverse-teammates-examining-statistics-infographics-computer_482257-90942.jpg_z2rlyq.jpg';

const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    inquiryType: '',
    tradingExperience: '',
    message: '',
    preferredContact: 'email',
    agreeToTerms: false
  });

  const [activeOffice, setActiveOffice] = useState(0);

  const contactMethods = [
    {
      icon: ChatBubbleLeftRightIcon,
      title: 'Live Chat',
      description: 'Get instant help with trading questions and platform support',
      contact: 'Start Chat',
      availability: '24/7 Available',
      responseTime: '< 30 seconds',
      color: 'bg-green-500'
    },
    {
      icon: VideoCameraIcon,
      title: 'Video Consultation',
      description: 'Schedule personalized sessions with account managers and trading coaches',
      contact: 'Book Session',
      availability: 'Business Hours',
      responseTime: 'Same Day',
      color: 'bg-purple-500'
    },
    {
      icon: EnvelopeIcon,
      title: 'Email Support',
      description: 'Detailed responses for complex inquiries and documentation requests',
      contact: 'support@crestedgebrokerage.com',
      availability: 'Always Open',
      responseTime: '< 2 hours',
      color: 'bg-orange-500'
    }
  ];

  const supportServices = [
    {
      icon: UserGroupIcon,
      title: 'Account Management',
      description: 'Dedicated relationship managers for premium accounts',
      features: ['Personal account manager', 'Priority support', 'Custom solutions', 'Regular check-ins']
    },
    {
      icon: AcademicCapIcon,
      title: 'Trading Education',
      description: 'Comprehensive learning resources and market insights',
      features: ['Live webinars', 'Trading courses', 'Market analysis', '1-on-1 coaching']
    },
    {
      icon: WrenchScrewdriverIcon,
      title: 'Technical Support',
      description: 'Platform assistance and troubleshooting',
      features: ['Platform training', 'API support', 'Mobile app help', 'System updates']
    },
    {
      icon: BoltIcon,
      title: 'Trading Support',
      description: 'Real-time assistance with trades and market access',
      features: ['Execution help', 'Market data issues', 'Order management', 'Risk management']
    }
  ];

  const globalOffices = [
    {
      city: 'Bolton',
      country: 'England',
      address: 'Flat B, 13 Torrisdale Close, Bolton, England, BL3 5RR',
      email: 'support@crestedgebrokerage.com',
      timezone: 'GMT (UTC+0)',
      image: officeImage,
      services: ['Headquarters', 'Trading Operations', 'Customer Support', 'Technology'],
      hours: 'Mon-Fri: 9:00 AM - 6:00 PM GMT'
    }
  ];

  const inquiryTypes = [
    'General Information',
    'Account Opening',
    'Trading Platform',
    'API Access',
    'Institutional Services',
    'Technical Support',
    'Compliance',
    'Partnership Opportunities'
  ];

  const tradingExperienceLevels = [
    'Beginner (< 1 year)',
    'Intermediate (1-3 years)',
    'Advanced (3-5 years)',
    'Expert (5+ years)',
    'Professional Trader',
    'Institutional'
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    // Handle form submission here
  };

  return (
    <div className="min-h-screen bg-white w-full overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative py-20 sm:py-32 bg-gradient-to-br from-primary-900 via-primary-800 to-primary-700 overflow-hidden mt-20">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-black/30"></div>
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
            style={{ backgroundImage: `url(${officeImage})` }}
          ></div>
        </div>
        
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-3xl sm:text-5xl lg:text-7xl font-bold text-white mb-8">
                Connect with
                <span className="block text-transparent bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text">
                  Trading Experts
                </span>
              </h1>
              <p className="text-lg sm:text-xl lg:text-2xl text-gray-200 max-w-4xl mx-auto leading-relaxed">
                Get personalized support from our global team of trading professionals, 
                available 24/7 to help you succeed in your trading journey.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-24 bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Multiple Ways to Connect
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choose your preferred communication method and get immediate access to our expert support team.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {contactMethods.map((method, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                className="group"
              >
                <div className="card hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 text-center">
                  <div className={`w-16 h-16 ${method.color} rounded-xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <method.icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{method.title}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">{method.description}</p>
                  
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center justify-center">
                      <ClockIcon className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-600">{method.availability}</span>
                    </div>
                    <div className="flex items-center justify-center">
                      <BoltIcon className="h-4 w-4 text-green-500 mr-2" />
                      <span className="text-sm font-medium text-green-600">{method.responseTime}</span>
                    </div>
                  </div>

                  <button className="w-full btn-primary text-sm">
                    {method.contact}
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Office Info */}
      <section className="py-24 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-8">
                Send Us a Message
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Fill out the form below and our team will get back to you within 2 hours during business hours.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    />
                  </div>

                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company/Organization
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Inquiry Type *
                    </label>
                    <select
                      name="inquiryType"
                      value={formData.inquiryType}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    >
                      <option value="">Select an option</option>
                      {inquiryTypes.map((type, index) => (
                        <option key={index} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Trading Experience
                    </label>
                    <select
                      name="tradingExperience"
                      value={formData.tradingExperience}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    >
                      <option value="">Select your level</option>
                      {tradingExperienceLevels.map((level, index) => (
                        <option key={index} value={level}>{level}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors resize-none"
                    placeholder="Please describe your inquiry in detail..."
                  ></textarea>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-4">
                    Preferred Contact Method
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {['email', 'chat', 'video'].map((method) => (
                      <label key={method} className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="preferredContact"
                          value={method}
                          checked={formData.preferredContact === method}
                          onChange={handleInputChange}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                        />
                        <span className="ml-2 text-sm text-gray-700 capitalize">{method}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="flex items-start">
                  <input
                    type="checkbox"
                    name="agreeToTerms"
                    checked={formData.agreeToTerms}
                    onChange={handleInputChange}
                    required
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mt-1"
                  />
                  <label className="ml-2 text-sm text-gray-600">
                    I agree to the{' '}
                    <a href="#" className="text-primary-600 hover:text-primary-700 underline">
                      Terms of Service
                    </a>{' '}
                    and{' '}
                    <a href="#" className="text-primary-600 hover:text-primary-700 underline">
                      Privacy Policy
                    </a>
                    *
                  </label>
                </div>

                <button
                  type="submit"
                  className="w-full btn-primary py-4 text-lg"
                  disabled={!formData.agreeToTerms}
                >
                  Send Message
                  <EnvelopeIcon className="ml-2 h-5 w-5" />
                </button>
              </form>
            </motion.div>

            {/* Office Information */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-8">
                Our Office Location
              </h2>

              {/* Office Details */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="card hover:shadow-xl transition-shadow duration-300"
              >
                <div className="relative mb-6">
                  <img 
                    src={globalOffices[0].image} 
                    alt={`${globalOffices[0].city} Office`}
                    className="w-full h-48 object-cover rounded-lg"
                  />
                  <div className="absolute inset-0 bg-gradient-to-tr from-primary-600/20 to-transparent rounded-lg"></div>
                </div>

                <div className="space-y-4">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      {globalOffices[0].city}, {globalOffices[0].country}
                    </h3>
                    <div className="flex items-start">
                      <MapPinIcon className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                      <span className="text-gray-600">{globalOffices[0].address}</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center">
                      <EnvelopeIcon className="h-5 w-5 text-gray-400 mr-3" />
                      <span className="text-gray-600">{globalOffices[0].email}</span>
                    </div>
                    <div className="flex items-center">
                      <ClockIcon className="h-5 w-5 text-gray-400 mr-3" />
                      <span className="text-gray-600">{globalOffices[0].hours}</span>
                    </div>
                    <div className="flex items-center">
                      <GlobeAltIcon className="h-5 w-5 text-gray-400 mr-3" />
                      <span className="text-gray-600">{globalOffices[0].timezone}</span>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Services Available:</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {globalOffices[0].services.map((service, idx) => (
                        <div key={idx} className="flex items-center">
                          <CheckBadgeIcon className="h-4 w-4 text-green-500 mr-2" />
                          <span className="text-sm text-gray-600">{service}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Support Services */}
      <section className="py-24 bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Comprehensive Support Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our dedicated support teams are here to help you succeed at every stage of your trading journey.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {supportServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                className="group"
              >
                <div className="card hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 text-center">
                  <div className="w-16 h-16 bg-primary-100 rounded-xl flex items-center justify-center mx-auto mb-6 group-hover:bg-primary-200 transition-colors duration-300">
                    <service.icon className="h-8 w-8 text-primary-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{service.title}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                  <div className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center">
                        <StarIcon className="h-4 w-4 text-yellow-500 mr-2" />
                        <span className="text-sm text-gray-600">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-primary-600 to-primary-700">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-8">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-gray-200 mb-12 max-w-3xl mx-auto leading-relaxed">
              Join over 250,000 traders who trust Crest Edge with their financial future. 
              Our team is ready to help you begin your trading journey today.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <motion.button 
                whileHover={{ scale: 1.05 }} 
                whileTap={{ scale: 0.95 }}
                className="bg-white text-primary-600 hover:bg-gray-100 px-10 py-4 rounded-lg font-bold text-lg transition-colors duration-300"
              >
                Open Account Today
                <UserGroupIcon className="ml-3 h-6 w-6 inline" />
              </motion.button>
              <motion.button 
                whileHover={{ scale: 1.05 }} 
                whileTap={{ scale: 0.95 }}
                className="border-2 border-white text-white hover:bg-white hover:text-primary-600 px-10 py-4 rounded-lg font-bold text-lg transition-all duration-300"
              >
                <CalendarDaysIcon className="mr-3 h-6 w-6 inline" />
                Schedule Demo
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact; 